/**
 * DraftManager
 * ------------
 * Zentrale Entwurfs-Sicherung: Verhindert Datenverlust, wenn EasyWork während
 * eines Deployments/Backend-Ausfalls neu geladen wird oder der Nutzer den Tab
 * schließt. Komponenten registrieren einen Snapshot-Callback; der Manager
 * schreibt ihn synchron in localStorage bei:
 *   - Event `easywork:save-drafts` (aus api.js bevor DeployOverlay erscheint)
 *   - `beforeunload` / `pagehide` (Tab schließen, Reload)
 *   - `visibilitychange` → hidden (Tab wechseln)
 *
 * Zusätzlich läuft ein periodischer Auto-Save alle 5 s.
 *
 * Nutzung siehe `hooks/useDraftPersistence.js`.
 */

const STORAGE_PREFIX = 'easywork:draft:';
const AUTOSAVE_INTERVAL_MS = 5000;
const MAX_DRAFT_AGE_MS = 24 * 3600 * 1000; // 24 h

const registry = new Map(); // storageKey -> getState()
let autosaveTimer = null;

const now = () => Date.now();

const writeToStorage = (key, data) => {
  try {
    const payload = { savedAt: now(), data };
    localStorage.setItem(STORAGE_PREFIX + key, JSON.stringify(payload));
  } catch (_e) {
    // Quota/Serialization → ignore, kein Blocker
  }
};

const readFromStorage = (key) => {
  try {
    const raw = localStorage.getItem(STORAGE_PREFIX + key);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return null;
    if (parsed.savedAt && now() - parsed.savedAt > MAX_DRAFT_AGE_MS) {
      localStorage.removeItem(STORAGE_PREFIX + key);
      return null;
    }
    return parsed;
  } catch (_e) {
    return null;
  }
};

const removeFromStorage = (key) => {
  try { localStorage.removeItem(STORAGE_PREFIX + key); } catch (_e) { /* noop */ }
};

/**
 * Registriert einen Snapshot-Getter. Rückgabewert: unsubscribe-fn.
 * @param {string} storageKey - eindeutiger Key (z.B. `email-composer:${threadId}`)
 * @param {() => any} getState - synchron aufrufbar, liefert serialisierbares Snapshot-Objekt
 */
export function registerDraft(storageKey, getState) {
  if (!storageKey || typeof getState !== 'function') return () => {};
  registry.set(storageKey, getState);
  return () => {
    // Nur unsubscribe, den Entwurf NICHT automatisch löschen —
    // dem Nutzer soll er beim nächsten Öffnen angeboten werden.
    if (registry.get(storageKey) === getState) registry.delete(storageKey);
  };
}

/**
 * Flusht alle registrierten Entwürfe synchron in localStorage.
 * Wird von notifyBackendUnavailable() gerufen, BEVOR das Overlay erscheint.
 */
export function flushAllDrafts(reason = 'manual') {
  registry.forEach((getState, key) => {
    try {
      const snapshot = getState();
      if (snapshot === null || snapshot === undefined) return;
      // Leere / bedeutungslose Entwürfe optional überspringen:
      if (snapshot && snapshot.__skipIfEmpty && snapshot.__isEmpty) return;
      writeToStorage(key, { ...snapshot, __reason: reason });
    } catch (_e) { /* Ein Fehler in einer Komponente darf andere nicht blocken */ }
  });
}

/** Entwurf abrufen (für Restore beim Mount). */
export function loadDraft(storageKey) {
  return readFromStorage(storageKey);
}

/** Entwurf löschen (nach erfolgreichem Speichern / bewusstem Verwerfen). */
export function clearDraft(storageKey) {
  removeFromStorage(storageKey);
}

/** Setup einmalig beim App-Start rufen (App.js). */
export function initDraftManager() {
  if (typeof window === 'undefined') return;
  if (window.__easyworkDraftManagerInit) return;
  window.__easyworkDraftManagerInit = true;

  // 1) Event aus api.js — vor DeployOverlay
  window.addEventListener('easywork:save-drafts', () => flushAllDrafts('backend-unavailable'));

  // 2) Tab schließen / Reload
  const flushForUnload = () => flushAllDrafts('unload');
  window.addEventListener('beforeunload', flushForUnload);
  window.addEventListener('pagehide', flushForUnload);

  // 3) Tab wechseln
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') flushAllDrafts('visibility-hidden');
  });

  // 4) Periodischer Auto-Save
  autosaveTimer = setInterval(() => flushAllDrafts('interval'), AUTOSAVE_INTERVAL_MS);
}

/** Nur für Tests. */
export function _teardownForTests() {
  if (autosaveTimer) clearInterval(autosaveTimer);
  autosaveTimer = null;
  registry.clear();
  if (typeof window !== 'undefined') window.__easyworkDraftManagerInit = false;
}
